import {
    BackgroundColor,
    type ColorParams,
    MultiPageImageModel,
    MultiPageVideoModel,
    TextElement,
    type VisualElement,
} from '@bynder-studio/render-core';
import { clone, equals, path } from 'rambda';
import { Gradient, SolidColor } from '@bynder-studio/misc';
import { isGradientColor } from 'packages/pages/editor/RightSideMenu/FormComponents/ColorPicker/utils';
import { BrandColor, BrandColorPalette, GradientBrandColor, SolidBrandColor } from 'packages/store/brandColors/types';
import { hexToRgb, rgbaToObject } from 'packages/common/editor/editorHelper';

type ElementColorProperties = { colorProperty: string; elementPropertyToUpdate: string };
type ElementColor = [ElementColorProperties, ColorParams];

const GRADIENT_INCOMPATIBLE_PROPERTIES = ['dropShadow'];

// Support only 2 levels of nesting. If you want more think about some sort of recursion or lodash _.set() alternative
const elementColorProperties: ElementColorProperties[] = [
    { colorProperty: '', elementPropertyToUpdate: 'color' }, // is case of BackgroundColor element
    { colorProperty: '', elementPropertyToUpdate: 'fillColor' },
    { colorProperty: '', elementPropertyToUpdate: 'borderColor' },
    { colorProperty: 'color', elementPropertyToUpdate: 'dropShadow' },
    { colorProperty: 'color', elementPropertyToUpdate: 'textBackground' },
    { colorProperty: 'borderColor', elementPropertyToUpdate: 'textBackground' },
];

// get all color properties from element with brand color attached
export const getElementColors = (element: VisualElement | BackgroundColor): ElementColor[] =>
    elementColorProperties.reduce((acc, properties) => {
        const { colorProperty, elementPropertyToUpdate } = properties;
        const colorPath = [elementPropertyToUpdate, colorProperty].filter((prop) => !!prop).join('.');
        const color: ColorParams | undefined = path(colorPath, element);

        if (color && color.brandColorId) {
            return [...acc, [properties, color]];
        }

        return acc;
    }, [] as ElementColor[]);

export const findBrandColorById = (id: number, palettes: BrandColorPalette[]): BrandColor | undefined =>
    palettes
        .reduce((acc, palette) => (palette.brandColors ? [...acc, ...palette.brandColors] : acc), [] as BrandColor[])
        .find((brandColor) => brandColor.id === id);

// Creates a new color object (solid or gradient) based on the brandColor type
export const createNewElementColor = (brandColor: BrandColor, elementColor: ColorParams): ColorParams => {
    if (isGradientColor(brandColor)) {
        const bc = brandColor as GradientBrandColor;

        return {
            type: bc.type,
            angle: bc.angle,
            stops: bc.stops,
            brandColorId: elementColor.brandColorId,
        };
    }

    const bc = brandColor as SolidBrandColor;

    const rgb = rgbaToObject(hexToRgb(bc.hexCode, bc.alpha));

    return {
        ...rgb,
        brandColorId: elementColor.brandColorId,
    };
};

// resolve a single color against the latest brand color palettes (no-op if it isn't brand-linked)
const resolveLinkedColor = (color: ColorParams, palettes: BrandColorPalette[]): ColorParams => {
    const brandColorId = color.brandColorId;

    if (!brandColorId) {
        return color;
    }

    const brandColor = findBrandColorById(brandColorId, palettes);

    if (!brandColor) {
        // Brand color not found, remove the reference
        return { ...color, brandColorId: null };
    }

    if (brandColor.deleted) {
        // Sync to latest color and detach the brandColorId
        const updatedColor = matchColors(color, brandColor);

        return updatedColor ? { ...updatedColor, brandColorId: null } : { ...color, brandColorId: null };
    }

    return createNewElementColor(brandColor, { ...color, brandColorId } as ColorParams);
};

export const updateTextColors = (
    formattedText: TextElement['formattedText'],
    palettes: BrandColorPalette[],
    includeStrokeColor = false,
): TextElement['formattedText'] => {
    const { runs } = formattedText;

    return {
        ...formattedText,
        runs: runs.map((item) => {
            const run = clone(item);

            run.color = resolveLinkedColor(run.color, palettes);

            // Stroke color is only user-settable behind the studioTextOutlineColor split, kept in
            // sync with that flag here too.
            if (includeStrokeColor && run.stroke?.strokeColor) {
                run.stroke = { ...run.stroke, strokeColor: resolveLinkedColor(run.stroke.strokeColor, palettes) };
            }

            return run;
        }),
    };
};

// find difference between element color and brand color. If there is no diff return nothing
export const matchColors = (elementColor: ColorParams, brandColor: BrandColor) => {
    if (isGradientColor(brandColor)) {
        const bc = brandColor as GradientBrandColor;
        const elColor = { ...elementColor } as Gradient;

        return equals(
            { type: bc.type, stops: bc.stops, angle: bc.angle },
            { type: elColor.type, stops: elColor.stops, angle: elColor.angle },
        )
            ? null
            : {
                  type: bc.type,
                  angle: bc.angle,
                  stops: bc.stops,
                  brandColorId: bc.id,
              };
    }

    const { red, green, blue, opacity } = elementColor as SolidColor;
    const elementColorRGBA = `rgba(${red},${green},${blue},${opacity})`;

    const bc = brandColor as SolidBrandColor;
    const brandColorRGBA = hexToRgb(bc.hexCode, bc.alpha);

    return brandColorRGBA === elementColorRGBA ? null : createNewElementColor(brandColor, elementColor);
};

// Support only 2 levels of nesting. If you want more think about some sort of recursion or lodash _.set() alternative
export const updateElementColor = (
    acc: any,
    properties: ElementColorProperties,
    element: any,
    elementColorToBeUpdated: any,
) => {
    if (properties.colorProperty) {
        acc[properties.elementPropertyToUpdate] = {
            ...(acc[properties.elementPropertyToUpdate] ?? element[properties.elementPropertyToUpdate]),
            [properties.colorProperty]: { ...elementColorToBeUpdated },
        };
    } else {
        acc[properties.elementPropertyToUpdate] = { ...elementColorToBeUpdated };
    }

    return acc;
};

// Match and update all color properties on every pageto up-to-date brand colors
export const syncBrandColors = (
    creativeModel: MultiPageVideoModel | MultiPageImageModel,
    palettes: BrandColorPalette[],
    save: () => void,
    includeStrokeColor = false,
) => {
    const originalPageIndex = creativeModel.getCurrentPageIndex();
    const pageCount = creativeModel.getModels().length;
    let isChanged = false;

    // Iterate through all pages
    for (let pageIndex = 0; pageIndex < pageCount; pageIndex++) {
        creativeModel.setCurrentPageIndex(pageIndex);

        [
            creativeModel.getBackgroundColor(),
            ...creativeModel.getAllElementsRecursively<VisualElement>('visual'),
        ].forEach((element) => {
            const elementColors = getElementColors(element);

            const elementColorsToBeUpdated = elementColors.reduce(
                (acc, elementColor) => {
                    const [properties, elementColorObj] = elementColor;
                    const brandColor = findBrandColorById(elementColorObj.brandColorId as number, palettes);

                    if (!brandColor) {
                        return updateElementColor(acc, properties, element, { ...elementColorObj, brandColorId: null });
                    }

                    // Gradients are not supported for certain properties (e.g. dropShadow).
                    // If the brand color was changed to a gradient, detach it and keep the original color.
                    if (
                        isGradientColor(brandColor) &&
                        GRADIENT_INCOMPATIBLE_PROPERTIES.includes(properties.elementPropertyToUpdate)
                    ) {
                        return updateElementColor(acc, properties, element, { ...elementColorObj, brandColorId: null });
                    }

                    const elementColorToBeUpdated = matchColors(elementColorObj, brandColor);

                    if (elementColorToBeUpdated) {
                        const colorToApply = elementColorToBeUpdated || elementColorObj;
                        const colorParams = {
                            ...colorToApply,
                        };

                        if (brandColor.deleted) {
                            colorParams.brandColorId = null;
                        }

                        return updateElementColor(acc, properties, element, colorParams);
                    }

                    return acc;
                },
                {} as Record<string, any>,
            );

            if (element instanceof TextElement) {
                const formattedText = element.getTextProps();
                const newFormattedText = updateTextColors(formattedText, palettes, includeStrokeColor);

                if (!equals(newFormattedText, formattedText)) {
                    elementColorsToBeUpdated.formattedText = newFormattedText;
                }
            }

            // update element only if it is necessary
            if (Object.keys(elementColorsToBeUpdated).length) {
                isChanged = true;

                if (element instanceof BackgroundColor) {
                    creativeModel.updateGlobalProperty('BACKGROUND_COLOR', elementColorsToBeUpdated);
                } else {
                    creativeModel.updateElement(element.id, elementColorsToBeUpdated);
                }
            }
        });
    }

    // Restore original page
    creativeModel.setCurrentPageIndex(originalPageIndex);

    if (isChanged) {
        save();
    }
};
